/*
 * The landing page is one face of a cube. Six plain HTML sections sit on a
 * preserve-3d parent; dragging pushes the camera back to reveal the box and
 * releasing snaps the nearest face flush with the screen (z = 0, so its text
 * renders 1:1 and stays crisp).
 *
 * Orientation is a quaternion, so a drag is completely free: both screen axes
 * at once, from any face, as far as you like. What keeps anyone from landing on
 * a sideways page is the *resting* set, not the drag — releasing slerps to the
 * upright orientation of whichever face is pointing at the camera, untwisting
 * whatever roll the tumble accumulated. Mid-drag anything goes; at rest the
 * page is always level, and it is always the page you were looking at.
 *
 * Coordinates are CSS's own: x right, y DOWN, z toward the viewer. The matrix
 * the spec gives for rotate3d() is the standard quaternion rotation matrix, so
 * these quaternions compose straight into the transform with no sign fudging.
 */

/* ------------------------------------------------------------- quaternions */

const qIdentity = { w: 1, x: 0, y: 0, z: 0 };

function qAxis(ax, ay, az, deg) {
  const half = (deg * Math.PI) / 360;
  const s = Math.sin(half);
  return { w: Math.cos(half), x: ax * s, y: ay * s, z: az * s };
}

const qRotX = (deg) => qAxis(1, 0, 0, deg);
const qRotY = (deg) => qAxis(0, 1, 0, deg);

// Hamilton product: the left operand is applied last, i.e. in screen space.
function qMul(a, b) {
  return {
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w
  };
}

const qDot = (a, b) => a.w * b.w + a.x * b.x + a.y * b.y + a.z * b.z;

function qNormalize(q) {
  const n = Math.hypot(q.w, q.x, q.y, q.z) || 1;
  return { w: q.w / n, x: q.x / n, y: q.y / n, z: q.z / n };
}

// Angle in degrees between two orientations (not between two quaternions —
// q and -q are the same rotation, hence the absolute value).
function qAngle(a, b) {
  return (2 * Math.acos(Math.min(1, Math.abs(qDot(a, b))))* 180) / Math.PI;
}

function qSlerp(a, b, t) {
  let dot = qDot(a, b);
  let end = b;
  if (dot < 0) {
    // Take the short way round.
    end = { w: -b.w, x: -b.x, y: -b.y, z: -b.z };
    dot = -dot;
  }
  if (dot > 0.9995) {
    return qNormalize({
      w: a.w + (end.w - a.w) * t,
      x: a.x + (end.x - a.x) * t,
      y: a.y + (end.y - a.y) * t,
      z: a.z + (end.z - a.z) * t
    });
  }
  const theta = Math.acos(dot);
  const sin = Math.sin(theta);
  const wa = Math.sin((1 - t) * theta) / sin;
  const wb = Math.sin(t * theta) / sin;
  return {
    w: a.w * wa + end.w * wb,
    x: a.x * wa + end.x * wb,
    y: a.y * wa + end.y * wb,
    z: a.z * wa + end.z * wb
  };
}

function qRotateVec(q, v) {
  const { w, x, y, z } = q;
  const [vx, vy, vz] = v;
  const tx = 2 * (y * vz - z * vy);
  const ty = 2 * (z * vx - x * vz);
  const tz = 2 * (x * vy - y * vx);
  return [
    vx + w * tx + (y * tz - z * ty),
    vy + w * ty + (z * tx - x * tz),
    vz + w * tz + (x * ty - y * tx)
  ];
}

function qToCss(q) {
  const n = qNormalize(q);
  const s = Math.sqrt(Math.max(0, 1 - n.w * n.w));
  if (s < 1e-6) return 'rotate3d(0, 0, 1, 0deg)';
  // acos(w) alone (no abs) already spans a full 0-360deg turn as w runs from
  // 1 to -1, so the axis needs no sign correction to match — folding the
  // angle into 0-180deg via abs(w) and then flipping the axis's sign to
  // compensate does *not* reproduce the same rotation except at exactly
  // 180deg, so turns whose live quaternion drifts past that (any turn method
  // that composes into an ambiguous half-angle) rendered as a different,
  // wrong-direction spin.
  const angle = (2 * Math.acos(clamp(n.w, -1, 1)) * 180) / Math.PI;
  return `rotate3d(${n.x / s}, ${n.y / s}, ${n.z / s}, ${angle.toFixed(3)}deg)`;
}

/* ------------------------------------------------------------------- faces */

// Each face's outward normal, and the orientation that brings it to the camera
// the right way up. Up is -y, so the top face needs a negative x rotation.
const FACES = {
  front: { normal: [0, 0, 1], rest: qIdentity, hash: '' },
  back: { normal: [0, 0, -1], rest: qRotY(180), hash: '#colophon' },
  right: { normal: [1, 0, 0], rest: qRotY(-90), hash: '#links' },
  left: { normal: [-1, 0, 0], rest: qRotY(90), hash: '#idlerambling', label: 'Substack' },
  top: { normal: [0, -1, 0], rest: qRotX(-90), hash: '#about-site' },
  bottom: { normal: [0, 1, 0], rest: qRotX(90), hash: '#about-me' }
};
const FACE_NAMES = Object.keys(FACES);

/*
 * Which face you land on is decided by which one is actually pointing at the
 * camera — never by which upright orientation is closest. Those differ when a
 * face is rolled more than a quarter turn: a page spun on the spot is still the
 * page you are looking at, so it unrolls in place rather than flying off to a
 * neighbour that happens to need less turning.
 */
function faceTowardCamera(q) {
  let best = 'front';
  let bestZ = -Infinity;
  for (const name of FACE_NAMES) {
    const z = qRotateVec(q, FACES[name].normal)[2]; // +z is toward the viewer
    if (z > bestZ) {
      bestZ = z;
      best = name;
    }
  }
  return best;
}

const DRAG_THRESHOLD = 8; // px before a gesture counts as a drag
const FLICK_MS = 130; // how far a release's momentum is extrapolated
const FLICK_MAX_DEG = 70; // ...and the most that extrapolation may add

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// Releasing a drag settles briskly; a turn you asked for (arrow, key, link)
// takes the scenic route — the camera pulls back so you watch it come round.
// Settling scales with how far there is to go, so unrolling half a turn reads
// as a deliberate flip rather than a jerk.
const snapDuration = (deg = 90) =>
  reduceMotion.matches ? 140 : clamp(260 + deg * 1.6, 300, 780);
const travelDuration = () => (reduceMotion.matches ? 160 : 950);
const TRAVEL_ZOOM = 0.92;

const scene = document.querySelector('.scene');
const cube = document.querySelector('.cube');
const canvas = document.getElementById('starfield');
const faceEls = new Map();
document.querySelectorAll('.face').forEach((el) => faceEls.set(el.dataset.face, el));

const supports3d =
  typeof CSS !== 'undefined' &&
  CSS.supports &&
  CSS.supports('transform-style', 'preserve-3d');

if (scene && cube && supports3d) {
  start();
}

function start() {
  document.documentElement.classList.add('cube-ready');

  // Geometry, recomputed on resize.
  let size = 0; // cube edge length in px
  let persp = 0; // scene perspective in px
  let restDolly = 0; // translateZ that puts the active face at z = 0
  let outDolly = 0; // translateZ that fits the whole cube on screen
  let degPerPxX = 0.25;
  let degPerPxY = 0.25;

  // Orientation. `rest` is the settled state, `live` is what's on screen.
  let restFace = 'front';
  let qRest = qIdentity;
  let qLive = qIdentity;
  let qDragBase = qIdentity; // orientation the current gesture started from
  let zoom = 0; // 0 = face flush with the screen, 1 = whole cube visible

  // Gesture bookkeeping.
  let pointerId = null;
  let dragging = false;
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let moved = false;
  let suppressClick = false;
  let velX = 0; // px/ms
  let velY = 0;
  let lastTime = 0;

  let anim = null;
  let frame = null;

  /* ---------------------------------------------------------------- layout */

  function measure() {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Comfortably larger than the viewport so neither face edges nor their inner
    // glow can peek in when a face is flush with the screen.
    size = Math.ceil(Math.max(vw, vh) * 1.2);
    persp = size * 1.45;
    restDolly = -size / 2;

    // Pull back far enough that the cube's widest silhouette (~1.73x its edge
    // when tumbling corner-on) still clears the short side of the viewport.
    const k = clamp((0.62 * Math.min(vw, vh)) / size, 0.12, 0.9);
    outDolly = -persp * (1 / k - 1);

    degPerPxX = 90 / (0.5 * vw);
    degPerPxY = 90 / (0.5 * vh);

    const root = document.documentElement;
    root.style.setProperty('--cube-size', `${size}px`);
    root.style.setProperty('--perspective', `${persp}px`);

    resizeCanvas();
    render();
  }

  window.addEventListener('resize', measure);
  window.addEventListener('orientationchange', measure);

  /* --------------------------------------------------------------- drawing */

  function render() {
    const dolly = restDolly + (outDolly - restDolly) * zoom;
    cube.style.transform = `translateZ(${dolly.toFixed(2)}px) ${qToCss(qLive)}`;
    if (canvas) canvas.style.opacity = String(Math.min(1, zoom * 1.4));
  }

  function tick(now) {
    frame = null;

    if (anim) {
      const t = clamp((now - anim.start) / anim.duration, 0, 1);
      const e = anim.travel ? easeInOutCubic(t) : easeOutCubic(t);
      qLive = qSlerp(anim.from, anim.to, e);
      // A travelling turn arcs out and back: the cube shrinks into view, turns,
      // then comes back flush with the screen.
      zoom = anim.travel
        ? Math.max(anim.fromZoom * (1 - t), Math.sin(Math.PI * t) * TRAVEL_ZOOM)
        : anim.fromZoom * (1 - e);
      if (t >= 1) {
        anim = null;
        qLive = qRest;
        zoom = 0;
      }
    } else if (dragging && moved) {
      // Only once the gesture is a real drag — pulling back on pointerdown
      // would slide a link out from under the cursor before it could be clicked.
      zoom += (1 - zoom) * 0.2;
    }

    render();
    drawStars(now);

    // Idle: nothing moving and the stars are hidden behind a flush face.
    if (anim || dragging || zoom > 0.001) schedule();
  }

  function schedule() {
    if (frame === null && !document.hidden) frame = requestAnimationFrame(tick);
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule();
  });

  /* ------------------------------------------------------------- rotation */

  // A drag is two screen-space turns composed: about the screen's vertical axis
  // for horizontal movement, about its horizontal axis for vertical movement.
  function orientationFor(dx, dy) {
    const turn = qMul(qRotX(-dy * degPerPxY), qRotY(dx * degPerPxX));
    return qMul(turn, qDragBase);
  }

  function settle(face, { animate = true, travel = false } = {}) {
    restFace = face;
    qRest = FACES[face].rest;
    updateFaceState();

    if (animate) {
      anim = {
        start: performance.now(),
        duration: travel ? travelDuration() : snapDuration(qAngle(qLive, qRest)),
        travel,
        from: qLive,
        to: qRest,
        fromZoom: zoom
      };
      schedule();
    } else {
      anim = null;
      qLive = qRest;
      zoom = 0;
      render();
    }
  }

  function updateFaceState() {
    faceEls.forEach((el, name) => {
      const isActive = name === restFace;
      el.toggleAttribute('inert', !isActive);
      el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    const hash = FACES[restFace].hash;
    const url = hash || `${location.pathname}${location.search}`;
    if ((location.hash || '') !== hash) history.replaceState(null, '', url);
    document.title = restFace === 'front' ? 'Side Quests' : `${labelFor(restFace)} — Side Quests`;
    updateArrows();
  }

  function labelFor(name) {
    if (FACES[name].label) return FACES[name].label;
    const el = faceEls.get(name);
    const heading = el && el.querySelector('h1, h2');
    return heading ? heading.textContent.trim() : 'Side Quests';
  }

  /* -------------------------------------------------- edge arrows (affordance) */

  const ARROW_TURNS = {
    left: () => qRotY(90),
    right: () => qRotY(-90),
    up: () => qRotX(-90),
    down: () => qRotX(90)
  };

  // Every face offers all four turns: a quarter turn that way says which face
  // comes forward, and settling on its upright orientation rights whatever roll
  // that leaves — the same self-righting the drag gets, just aimed.
  function destinationFor(dir) {
    const face = faceTowardCamera(qMul(ARROW_TURNS[dir](), qRest));
    return face === restFace ? null : face;
  }

  function updateArrows() {
    document.querySelectorAll('.edge-arrow').forEach((button) => {
      const face = destinationFor(button.dataset.dir);
      button.hidden = !face;
      if (face) {
        const label = labelFor(face);
        button.querySelector('.edge-label').textContent = label;
        button.setAttribute('aria-label', `Turn to ${label}`);
      }
    });
  }

  document.querySelectorAll('.edge-arrow').forEach((button) => {
    button.addEventListener('click', () => {
      const face = destinationFor(button.dataset.dir);
      if (face) settle(face, { travel: true });
    });
  });

  /* ---------------------------------------------------------------- input */

  scene.addEventListener('pointerdown', (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (pointerId !== null) return;
    // The archive needs ordinary scrolling and taps; the surrounding area of
    // the face still tumbles the cube.
    if (event.target.closest('.idlerambling-scroll')) return;
    pointerId = event.pointerId;
    dragging = true;
    moved = false;
    velX = 0;
    velY = 0;
    startX = event.clientX;
    startY = event.clientY;
    lastX = event.clientX;
    lastY = event.clientY;
    lastTime = performance.now();
    anim = null;
    // Pick the tumble up from wherever the cube actually is, even mid-flight.
    qDragBase = qLive;
    scene.classList.add('is-dragging');
    document.documentElement.classList.add('is-dragging');
    schedule();
  });

  scene.addEventListener('pointermove', (event) => {
    if (!dragging || event.pointerId !== pointerId) return;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;

    if (!moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      moved = true;
      if (scene.setPointerCapture) {
        // Captured only once it's a real drag, so a plain click still hits links.
        try {
          scene.setPointerCapture(pointerId);
        } catch (err) {
          /* capture is a nicety; drags still work without it */
        }
      }
    }

    event.preventDefault();

    const now = performance.now();
    const dt = now - lastTime;
    if (dt > 0) {
      velX = (event.clientX - lastX) / dt;
      velY = (event.clientY - lastY) / dt;
    }
    lastX = event.clientX;
    lastY = event.clientY;
    lastTime = now;

    qLive = orientationFor(dx, dy);
    schedule();
  });

  scene.addEventListener('pointerup', endDrag);
  scene.addEventListener('pointercancel', endDrag);

  function endDrag(event) {
    if (!dragging || (event && event.pointerId !== pointerId)) return;
    dragging = false;
    scene.classList.remove('is-dragging');
    document.documentElement.classList.remove('is-dragging');
    suppressClick = moved;
    if (pointerId !== null && scene.releasePointerCapture) {
      try {
        scene.releasePointerCapture(pointerId);
      } catch (err) {
        /* already released */
      }
    }
    pointerId = null;

    if (!moved) {
      // A tap: nothing turned, so there's nothing to settle back.
      if (zoom > 0) settle(restFace);
      return;
    }
    moved = false;

    // A pointer that came to rest before release shouldn't flick.
    if (performance.now() - lastTime > 90) {
      velX = 0;
      velY = 0;
    }

    // Carry the throw a little further before deciding which face won, so a
    // flick can finish a turn the fingers only started.
    const flickX = clamp(velX * FLICK_MS * degPerPxX, -FLICK_MAX_DEG, FLICK_MAX_DEG);
    const flickY = clamp(velY * FLICK_MS * degPerPxY, -FLICK_MAX_DEG, FLICK_MAX_DEG);
    const predicted = qMul(qMul(qRotX(-flickY), qRotY(flickX)), qLive);

    settle(faceTowardCamera(predicted));
  }

  // Swallow the click that ends a drag so dragging across a link doesn't navigate.
  scene.addEventListener(
    'click',
    (event) => {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }
    },
    true
  );

  // In-page controls: `<a data-face="top">` turns the cube instead of navigating.
  scene.addEventListener('click', (event) => {
    const target = event.target.closest('[data-face]');
    if (!target || target.classList.contains('face')) return;
    event.preventDefault();
    settle(FACES[target.dataset.face] ? target.dataset.face : 'front', { travel: true });
  });

  window.addEventListener('keydown', (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const tag = event.target && event.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (event.key === 'Escape' || event.key === 'Home') {
      settle('front', { travel: true });
      event.preventDefault();
      return;
    }

    // Arrow keys take the same turns, under the same rules, as the edge arrows.
    const dir = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' }[
      event.key
    ];
    if (!dir) return;
    const face = destinationFor(dir);
    if (!face) return;
    settle(face, { travel: true });
    event.preventDefault();
  });

  window.addEventListener('hashchange', () => {
    const face = faceFromHash();
    if (face !== restFace) settle(face, { travel: true });
  });

  function faceFromHash() {
    const hash = location.hash;
    for (const name of FACE_NAMES) {
      if (FACES[name].hash && FACES[name].hash === hash) return name;
    }
    return 'front';
  }

  /* ------------------------------------------------------------ starfield */

  const LAYERS = [
    { count: 110, size: 0.8, alpha: 0.5, depth: 0.25 },
    { count: 70, size: 1.3, alpha: 0.75, depth: 0.55 },
    { count: 35, size: 2, alpha: 1, depth: 1 }
  ];

  const ctx = canvas ? canvas.getContext('2d') : null;
  const stars = [];
  for (const layer of LAYERS) {
    for (let i = 0; i < layer.count; i += 1) {
      stars.push({
        x: Math.random(),
        y: Math.random(),
        size: layer.size * (0.7 + Math.random() * 0.6),
        alpha: layer.alpha,
        depth: layer.depth,
        phase: Math.random() * Math.PI * 2,
        twinkle: 0.4 + Math.random() * 0.8
      });
    }
  }

  function resizeCanvas() {
    if (!canvas || !ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function drawStars(now) {
    if (!ctx || !canvas) return;
    const opacity = Math.min(1, zoom * 1.4);
    if (opacity <= 0.01) return;

    const w = window.innerWidth;
    const h = window.innerHeight;
    const still = reduceMotion.matches;
    const drift = still ? 0 : now * 0.000004;
    // Stars slide against the cube's turn, which sells the parallax. Where the
    // front face is now pointing stands in for how far it has been turned.
    const facing = qRotateVec(qLive, [0, 0, 1]);
    const shiftX = facing[0] * 0.09;
    const shiftY = facing[1] * 0.09;

    ctx.clearRect(0, 0, w, h);
    for (const star of stars) {
      const x = wrap(star.x + drift * star.depth + shiftX * star.depth) * w;
      const y = wrap(star.y + shiftY * star.depth) * h;
      const flicker = still ? 1 : 0.75 + 0.25 * Math.sin(now * 0.001 * star.twinkle + star.phase);
      ctx.globalAlpha = star.alpha * flicker;
      ctx.fillStyle = star.depth > 0.8 ? '#eaffea' : '#a8e6a8';
      ctx.beginPath();
      ctx.arc(x, y, star.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  // Runs last: sizing the canvas needs the star setup above to exist.
  measure();
  settle(faceFromHash(), { animate: false });
}

/* ----------------------------------------------------------------- helpers */

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function wrap(value) {
  return ((value % 1) + 1) % 1;
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
